import React, { useEffect, useState } from "react";
import { Button, Steps, Radio } from "antd";
import { CheckOutlined, LoadingOutlined, BankOutlined, CreditCardOutlined } from "@ant-design/icons";
import axios from "axios";
import ENVIRONMENT from '../../../../../../environments';
import ContractConstants from "../../../../AdminComponents/CustomerContractForm/ContractConstants";
import "../PlanActivationAndPayment/PlanActivationAndPayment.scss";

const { Step } = Steps;

const ContractActivationAndPayment = ({
    contractDetails,
    cardStatus,
    closeSubscriptionUpgrade,
    loadUserRequestStatus,
    setSuccessModalVisible = () => {},
    setShowPaymentFailureModal = () => {},
    setSystemErrorModal = () => {},
    setSubscriptionPaymentProcessing = () => {},
    setPartialCreditsGiven = () => {},
    onSuccess = () => {}
}) => {
    const [stage, setStage] = useState(1);
    const [paymentLoader, setPaymentLoader] = useState(false);
    const [paymentInformation, setPaymentInformation] = useState(null);
    const [deleteCardFlow, setDeleteCardFlow] = useState(false);
    const [cardSaveFlowTriggered, setCardSaveFlowTriggered] = useState(false);
    const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);

    const getPaymentScheduleDisplay = (schedule) => {
        if (!schedule) return null;
        const normalizedSchedule = schedule === 'every_6_months' ? '6_months' : schedule;
        return ContractConstants.USAGE_DURATION_HEADING[normalizedSchedule] || schedule;
    };

    const getPaymentSchedulePeriod = (schedule) => {
        if (!schedule) return null;
        if (schedule === 'monthly') return 'month';
        if (schedule === 'quarterly') return 'quarter';
        if (schedule === 'every_6_months' || schedule === '6_months') return '6 mths';
        if (schedule === 'annually') return 'year';
        return schedule;
    };

    const getPaymentAmount = () => {
        return parseFloat(contractDetails?.['payment_schedule_price'] || contractDetails?.['contract_schedule_payment'] || 0);
    };

    const fetchPaymentInformation = () => {
        let payload = {
            "username": localStorage.getItem('username'),
            "action": "getinfo"
        };
        axios.post(ENVIRONMENT.GET_USER_PAYMENT_INFORMATION, payload)
            .then(res => {
                const info = res.data['card_information'];
                setPaymentInformation(info);
                if (info && info.length > 0) {
                    if (selectedPaymentMethod && info.some(c => c.id === selectedPaymentMethod)) {
                        // keep current selection
                    } else {
                        setSelectedPaymentMethod(info[0].id);
                    }
                }
            });
    };

    useEffect(() => {
        fetchPaymentInformation();
    }, []);

    const initiateConfirmation = (deleteFlow = false) => {
        window.scrollTo({
            top: 0,
            behavior: 'smooth'
        });
          
        if ((cardStatus == 1) && !deleteFlow) {
            setStage(2);
            return;
        }

        window.addEventListener('card_saved', (e) => {
            if(deleteCardFlow){
                axios.post(ENVIRONMENT.GET_USER_PAYMENT_INFORMATION, {
                    'username': localStorage.getItem('username'),
                    'action': 'delete_previous'
                }).then((res) => {
                    setStage(1);
                    setDeleteCardFlow(false);
                });
            }
            let action = e.detail.action;
            setCardSaveFlowTriggered(false);
            if (action == 'save_card') {
                loadUserRequestStatus();
                fetchPaymentInformation();
                setStage(2);
            }
        }, false);

        if (cardStatus == 0 || deleteFlow) {
            let child_window = window.open('/save-card?action=save_card', '', "width=600,height=600");
            let refreshInterval = setInterval(() => {
                if(child_window.closed) {
                    setCardSaveFlowTriggered(false);
                    clearInterval(refreshInterval);
                }
            }, 500);
            setCardSaveFlowTriggered(true);
        }
    };

    const changePaymentMethod = () => {
        setDeleteCardFlow(true);
        initiateConfirmation(true);
    };

    const handleContractActivation = () => {
        setPaymentLoader(true);
        const paymentAmount = getPaymentAmount();
        
        let payload = {
            subscription_type: 'custom_contract',
            contract_id: contractDetails['id'],
            username: localStorage.getItem('username'),
            contract_payment: true,
            activate_subscription: true,
            payment_method_stripe_id: selectedPaymentMethod
        };
        if(contractDetails?.['contract_subscription']){
            payload['subscription_type'] = contractDetails?.['contract_subscription'];
        }

        axios.post(ENVIRONMENT.UPGRADE_SUBSCRIPTION, payload)
            .then(res => {
                setPaymentLoader(false);
                closeSubscriptionUpgrade();
                if(res.data && res.data.status_code === 200){
                    loadUserRequestStatus();
                    if(res.data.payment_status == 'processing'){
                        setSubscriptionPaymentProcessing(true);
                    }
                    if(res.data.credits_pct_given){
                        setPartialCreditsGiven(res.data.credits_pct_given * 100);
                    }
                    onSuccess();
                    setSuccessModalVisible(true);
                } else {
                    if(res.data?.error_type == 'payment_failed')
                        setShowPaymentFailureModal(true);
                    else
                        setSystemErrorModal(true);
                }
            })
            .catch(error => {
                setPaymentLoader(false);
                console.error('Contract activation error:', error);
                setSystemErrorModal(true);
            });
    };

    const customDot = (dot, { status }) => {
        switch (status) {
            case "process":
                return <div className="custom-dot-process" />;
            case "finish":
                return (
                    <div className="custom-dot-finish">
                        <CheckOutlined className="check-icon" />
                    </div>
                );
            default:
                return dot;
        }
    };

    const paymentAmount = getPaymentAmount();

    return (
        <div style={{ width: '100%' }}>
            <div className="steps-tab-register">
                <Steps 
                    progressDot={customDot}
                    labelPlacement="vertical" 
                    className='step-class' 
                    style={{ marginBottom: 0, padding: '32px 48px' }} 
                    size="small" 
                    current={stage}
                >
                    <Step className={`manrope f-14`} title={<span className="manrope f-14 w-500 lh-22 black-085">Review Order</span>} />
                    <Step className={`manrope f-14`} title={<span className="manrope f-14 w-500 lh-22 black-085">Checkout</span>} />
                </Steps>
            </div>

            <div className="selected-plan-heading-gradient">
                <div className="manrope f-30 w-700 black-085 lh-32">
                    Contract Activation
                </div>
                <div className="manrope f-16 w-500 black-085 lh-24 mt-8">
                    Activate your contract to gain access to the platform
                </div>
            </div>

            <div className="subscription-card" style={{ padding: '32px 25px', borderRadius: '4px' }}>
                {stage == 1 ? (
                    <>
                        <div className="price-section" style={{ marginBottom: 8, alignItems: 'baseline', flexWrap: 'nowrap' }}>
                            <span style={{ whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'baseline' }}>
                                <span className="manrope f-26 w-700 black-085" style={{ lineHeight: '1' }}>
                                    ${paymentAmount.toLocaleString()}
                                </span>
                                {contractDetails && contractDetails['payment_schedule'] && (
                                    <span className="manrope f-14 w-500 black-085" style={{ lineHeight: '1' }}>
                                        /{getPaymentSchedulePeriod(contractDetails['payment_schedule'])}
                                    </span>
                                )}
                            </span>
                            {contractDetails && contractDetails['payment_schedule'] && (
                                <span className="manrope f-14 w-400 lh-22 gray-045">
                                    Billed {getPaymentScheduleDisplay(contractDetails['payment_schedule']).toLowerCase()}
                                </span>
                            )}
                        </div>
                        <div className="gray-billing-bg">
                            <div className="justify-space-between" style={{ marginBottom: 8 }}>
                                <span className="manrope f-14 w-600 lh-22 black-085">Your billing cycle</span>
                                <span className="manrope f-14 w-600 lh-22 black-085">
                                    ${paymentAmount.toLocaleString()} billed {contractDetails && contractDetails['payment_schedule'] 
                                        ? getPaymentScheduleDisplay(contractDetails['payment_schedule']).toLowerCase()
                                        : 'one-time'}
                                </span>
                            </div>
                        </div>
                        <div className="subscription-note" style={{ marginTop: 24, textAlign: 'center' }}>
                            <span>
                                Make the payment to activate your contract and gain access to the platform.
                            </span>
                        </div>
                        <div style={{ display: 'flex', gap: '12px', marginTop: 24 }}>
                            <Button className="back-btn" style={{ flex: '0 0 25%' }} onClick={closeSubscriptionUpgrade}>
                                Cancel
                            </Button>
                            <Button 
                                className="checkout-btn" 
                                style={{ flex: '0 0 calc(75% - 12px)' }} 
                                onClick={() => initiateConfirmation()}
                            >
                                {cardStatus == 1 ? 'Continue to checkout' : 'Add Payment Method'}
                            </Button>
                        </div>
                    </>
                ) : (
                    <div>
                        {cardStatus == 1 && paymentInformation != null ? (
                            <div>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
                                    <div className="manrope f-16 w-600 black-085">Select Payment Method</div>
                                    <a onClick={changePaymentMethod} className="manrope f-16 w-400 lh-24 blue" style={{ cursor: 'pointer' }}>
                                        Add Payment Method
                                    </a>
                                </div>
                                <div style={{ marginBottom: 24 }}>
                                    <Radio.Group onChange={(e) => setSelectedPaymentMethod(e.target.value)} value={selectedPaymentMethod} style={{ width: '100%' }}>
                                        {paymentInformation.map((info) => (
                                            <div key={info.id} className="payment-method-row" style={{ display: 'flex', alignItems: 'center', marginBottom: 16, border: '1px solid #d9d9d9', padding: '12px', borderRadius: '4px' }}>
                                                <Radio value={info.id} style={{ marginRight: 16 }} />
                                                <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                                    {info.type === 'us_bank_account' ? (
                                                        <>
                                                            <BankOutlined style={{ fontSize: '20px', color: '#1890ff', marginRight: '24px' }} />
                                                            <span className="manrope f-16 w-600 lh-24 black-085">
                                                                {info.us_bank_account.bank_name}
                                                            </span>
                                                            <span className="manrope f-16 w-600 lh-24 black-085" style={{ marginLeft: 'auto' }}>
                                                                ****{info.us_bank_account.last4}
                                                            </span>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <CreditCardOutlined style={{ fontSize: '20px', color: '#1890ff', marginRight: '24px' }} />
                                                            <span className="manrope f-16 w-600 lh-24 black-085" style={{ marginRight: '24px' }}>
                                                                {info.card.brand.charAt(0).toUpperCase() + info.card.brand.slice(1)}
                                                            </span>
                                                            <span className="manrope f-16 w-600 lh-24 black-085">
                                                                ****{info.card.last4}
                                                            </span>
                                                            <span className="manrope f-16 w-400 lh-24 black-085" style={{ marginLeft: 'auto' }}>
                                                                Expires {String(info.card.exp_month).padStart(2, "0")}/{info.card.exp_year}
                                                            </span>
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </Radio.Group>
                                </div>
                                
                                <div style={{ display: 'flex', gap: '12px', marginTop: 24 }}>
                                    <Button onClick={() => setStage(1)} className="back-btn" style={{ flex: '0 0 25%' }}>
                                        Back
                                    </Button>
                                    <Button 
                                        onClick={handleContractActivation} 
                                        disabled={paymentLoader} 
                                        className="checkout-btn" 
                                        style={{ flex: '0 0 calc(75% - 12px)' }}
                                    >
                                        {paymentLoader ? <LoadingOutlined className="manrope f-14" /> : ''} Pay ${paymentAmount.toLocaleString()}
                                    </Button>
                                </div>

                                <div className="subscription-note" style={{ marginTop: 24, textAlign: 'center' }}>
                                    <span>
                                        Make the payment to activate your contract and gain access to the platform.
                                    </span>
                                </div>
                            </div>
                        ) : (
                            <LoadingOutlined className="manrope f-16" />
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default ContractActivationAndPayment;
